/**
 * Custom React hook to calculate smart column widths for a table component.
 *
 * This hook dynamically determines the width of each column based on the current table width,
 * the number of columns, and any manually specified column widths. It observes the table's
 * size using a ResizeObserver and recalculates widths when the table is resized.
 *
 * - The first and last columns are assigned a fixed width.
 * - Middle columns share the remaining width equally, unless a manual width is specified for a column.
 * - If a column index is present in `manualColumnWidthsIndex`, its width is set from `manualColumnWidths`.
 * - The last column's width is slightly reduced (by 20px) compared to other middle columns.
 *
 * @param {number[]} manualColumnWidthsIndex - Array of column indices that have manually set widths.
 * @param {number[]} manualColumnWidths - Array of manual widths corresponding to indices in `manualColumnWidthsIndex`.
 * @param {Array} columns - Array of column definitions for the table.
 * @param {Array} data - Array of table data (not used in width calculation, but may be useful for future extensions).
 * @param {React.RefObject} tableRef - Ref object pointing to the table DOM element.
 * @returns {number[]} Array of calculated widths for each column in the table.
 */
import { useMemo, useEffect, useState } from 'react';

export const useSmartColumnWidths = (manualColumnWidthsIndex, manualColumnWidths, columns, data, tableRef, tableRefContent) => {
  const [tableWidth, setTableWidth] = useState(0);
  const [tableWidthContent, setTableWidthContent] = useState(0);

  useEffect(() => {
    if (!tableRef.current || typeof ResizeObserver === 'undefined') return;

    let observer;
    try {
      observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          if (entry.contentRect) {
            setTableWidth(entry.contentRect.width);
          }
        }
      });
      observer.observe(tableRef.current);
    } catch (error) {
      console.error('ResizeObserver error (possibly blocked by SES):', error);
    }

    return () => {
      try {
        if (observer && tableRef.current) {
          observer.unobserve(tableRef.current);
          observer.disconnect();
        }
      } catch (e) {
        console.error('Error during ResizeObserver cleanup:', e);
      }
    };
  }, [tableRef]);

  /**
   * Calculates the widths for each column in a table based on the total table width,
   * the number of columns, and any manually set column widths.
   *
   * - The first and last columns are assigned a fixed width (`firstLastWidth`).
   * - Middle columns share the remaining width equally, unless a manual width is specified.
   * - If a column index is found in `manualColumnWidthsIndex`, its width is set from `manualColumnWidths`.
   * - The last column's width is slightly reduced (by 20px) compared to other middle columns.
   *
   * @returns {number[]} An array of column widths corresponding to each column in the table.
   */
  const widths = useMemo(() => {
    if (!tableWidth || columns.length === 0) return [];

    const numColumns = columns.length;
    const firstLastWidth = 70;

    // If only 1 or 2 columns, both are fixed
    if (numColumns <= 2) {
      return Array.from({ length: numColumns }, () => firstLastWidth);
    }

    const middleColumnCount = numColumns - 2;
    const remainingWidth = tableWidth - (2 * firstLastWidth);
    const middleWidth = Math.floor(remainingWidth / middleColumnCount);

    // Build base widths
    const base = columns.map((_, index) => {
      const isFirst = index === 0;
      const isLast = index === numColumns - 1;

      // if (isFirst || isLast) return firstLastWidth;
      if (isFirst) return firstLastWidth;
      if (isLast) return firstLastWidth+30;

      const manualIndex = (manualColumnWidthsIndex || []).indexOf(index);
      if (manualIndex !== -1) return manualColumnWidths[manualIndex];

      return middleWidth;
    });

    // Compute current sum and remainder to distribute
    let sum = base.reduce((a, b) => a + b, 0);
    let remainder = tableWidth - sum;

    if (remainder <= 0) return base; // nothing to add or already over

    // Find adjustable columns: middle columns that are NOT manual
    const manualSet = new Set(manualColumnWidthsIndex || []);
    const adjustable = [];
    for (let i = 1; i < numColumns - 1; i++) {
      if (!manualSet.has(i)) adjustable.push(i);
    }

    if (adjustable.length === 0) {
      // No adjustable columns available; can't touch manual or first/last => return as-is
      return base;
    }

    // Evenly distribute remainder across adjustable columns
    const per = Math.floor(remainder / adjustable.length);
    let extra = remainder % adjustable.length;

    const out = base.slice();
    for (const idx of adjustable) {
      out[idx] += per + (extra > 0 ? 1 : 0);
      if (extra > 0) extra--;
    }

    return out;
  }, [columns, tableWidth, manualColumnWidthsIndex, manualColumnWidths]);
  return widths;
};
